import { useState } from "react";

import Field from "../../../shared/ui/Field/Field";
import TextInput from "../../../shared/ui/Field/TextInput";
import Select from "../../../shared/ui/Field/Select";
import Textarea from "../../../shared/ui/Field/Textarea";
import InlineError from "../../../shared/ui/Field/InlineError";
import { memberDisplayName } from "../../companyWorkspace/lib/companyLabels";

const TYPE_OPTIONS = [
  { value: "buyer", label: "Покупець" },
  { value: "seller", label: "Продавець" },
  { value: "both", label: "Покупець і продавець" },
];

const STATUS_OPTIONS = [
  { value: "lead", label: "Лід" },
  { value: "active", label: "Активний" },
  { value: "inactive", label: "Неактивний" },
  { value: "closed", label: "Закритий" },
];

const RESPONSIBILITY_MODES = [
  { value: "self", label: "Я відповідальний" },
  { value: "other", label: "Інший учасник" },
  { value: "unassigned", label: "Без відповідального" },
];

const INITIAL = {
  first_name: "",
  last_name: "",
  phone: "",
  email: "",
  type: "buyer",
  status: "lead",
  source: "",
  notes: "",
};

export const CORE_FIELD_KEYS = [
  "first_name",
  "last_name",
  "phone",
  "email",
  "type",
  "status",
  "source",
  "notes",
];

function buildMemberOptions(members) {
  const list = Array.isArray(members) ? members : [];
  return [
    { value: "", label: "Обрати учасника" },
    ...list
      .filter((m) => m?.user?.id != null)
      .map((m) => ({
        value: String(m.user.id),
        label: memberDisplayName(m),
      })),
  ];
}

function CompanyClientCreateModal({
  busy,
  error,
  fieldErrors = {},
  onClose,
  onSubmit,
  onFieldEdit,
  canSelectResponsibility = false,
  members = [],
}) {
  const [values, setValues] = useState(INITIAL);
  const [localError, setLocalError] = useState(null);
  const [responsibilityMode, setResponsibilityMode] = useState("self");
  const [responsibleUserId, setResponsibleUserId] = useState("");

  const setField = (key) => (event) => {
    setValues((prev) => ({ ...prev, [key]: event.target.value }));
    if (typeof onFieldEdit === "function") {
      onFieldEdit(key);
    }
  };

  const handleClose = () => {
    if (busy) return;
    setResponsibilityMode("self");
    setResponsibleUserId("");
    setLocalError(null);
    onClose();
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (busy) return;
    if (!(values.first_name || "").trim()) {
      setLocalError("Введіть ім'я.");
      return;
    }
    if (!(values.phone || "").trim()) {
      setLocalError("Введіть телефон.");
      return;
    }
    if (canSelectResponsibility && responsibilityMode === "other") {
      const parsed = Number(responsibleUserId);
      if (!responsibleUserId || !Number.isFinite(parsed) || parsed <= 0) {
        setLocalError("Оберіть учасника.");
        return;
      }
    }
    setLocalError(null);
    const body = {
      first_name: values.first_name.trim(),
      last_name: (values.last_name || "").trim(),
      phone: values.phone.trim(),
      email: (values.email || "").trim(),
      type: values.type,
      status: values.status,
      source: (values.source || "").trim(),
      notes: (values.notes || "").trim(),
    };
    if (canSelectResponsibility) {
      if (responsibilityMode === "other") {
        body.responsible_user_id = Number(responsibleUserId);
      } else if (responsibilityMode === "unassigned") {
        body.responsible_user_id = null;
      }
      // mode "self" → omit responsible_user_id
    }
    // MEMBER: omit responsible_user_id entirely
    onSubmit(body);
  };

  const memberOptions = buildMemberOptions(members);
  const formError = localError || fieldErrors._form || error;

  return (
    <div
      className="feature-clients-list__modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="company-client-create-title"
    >
      <div
        className="feature-clients-list__modal-backdrop"
        aria-hidden="true"
        onClick={handleClose}
      />
      <div className="feature-clients-list__modal-dialog">
        <header className="feature-clients-list__modal-head">
          <h2
            id="company-client-create-title"
            className="feature-clients-list__modal-title"
          >
            <i className="fa-solid fa-user-plus" aria-hidden="true" />
            <span>Новий клієнт компанії</span>
          </h2>
          <button
            type="button"
            className="feature-clients-list__modal-close"
            onClick={handleClose}
            disabled={busy}
            aria-label="Закрити вікно створення"
            title="Закрити"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </header>
        <div className="feature-clients-list__modal-body">
          <form onSubmit={handleSubmit}>
            <Field id="cc-first-name" label="Ім'я" required error={fieldErrors.first_name}>
              <TextInput
                name="first_name"
                value={values.first_name}
                onChange={setField("first_name")}
                disabled={busy}
              />
            </Field>
            <Field id="cc-last-name" label="Прізвище" error={fieldErrors.last_name}>
              <TextInput
                name="last_name"
                value={values.last_name}
                onChange={setField("last_name")}
                disabled={busy}
              />
            </Field>
            <Field id="cc-phone" label="Телефон" required error={fieldErrors.phone}>
              <TextInput
                name="phone"
                value={values.phone}
                onChange={setField("phone")}
                disabled={busy}
              />
            </Field>
            <Field id="cc-email" label="Email" error={fieldErrors.email}>
              <TextInput
                name="email"
                value={values.email}
                onChange={setField("email")}
                disabled={busy}
              />
            </Field>
            <Field id="cc-type" label="Тип" error={fieldErrors.type}>
              <Select
                name="type"
                value={values.type}
                onChange={setField("type")}
                options={TYPE_OPTIONS}
                disabled={busy}
              />
            </Field>
            <Field id="cc-status" label="Статус" error={fieldErrors.status}>
              <Select
                name="status"
                value={values.status}
                onChange={setField("status")}
                options={STATUS_OPTIONS}
                disabled={busy}
              />
            </Field>
            <Field id="cc-source" label="Джерело" error={fieldErrors.source}>
              <TextInput
                name="source"
                value={values.source}
                onChange={setField("source")}
                disabled={busy}
              />
            </Field>
            <Field id="cc-notes" label="Нотатки" error={fieldErrors.notes}>
              <Textarea
                name="notes"
                value={values.notes}
                onChange={setField("notes")}
                rows={3}
                disabled={busy}
              />
            </Field>

            {canSelectResponsibility ? (
              <fieldset className="feature-company-clients__responsibility">
                <legend>Відповідальний</legend>
                <div
                  className="clients-filter-group feature-company-clients__responsibility-modes"
                  role="radiogroup"
                  aria-label="Відповідальний"
                >
                  {RESPONSIBILITY_MODES.map((mode) => (
                    <button
                      key={mode.value}
                      type="button"
                      className={[
                        "filter-chip",
                        responsibilityMode === mode.value
                          ? "filter-chip--active"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      aria-pressed={responsibilityMode === mode.value}
                      disabled={busy}
                      onClick={() => {
                        setResponsibilityMode(mode.value);
                        if (mode.value !== "other") {
                          setResponsibleUserId("");
                        }
                      }}
                    >
                      {mode.label}
                    </button>
                  ))}
                </div>
                {responsibilityMode === "other" ? (
                  <Field id="cc-responsible-user" label="Учасник" required>
                    <Select
                      name="responsible_user_id"
                      value={responsibleUserId}
                      onChange={(event) =>
                        setResponsibleUserId(event.target.value)
                      }
                      options={memberOptions}
                      disabled={busy}
                    />
                  </Field>
                ) : null}
              </fieldset>
            ) : (
              <p className="feature-company-clients__owner-note">
                Після створення ви будете відповідальним за цього клієнта.
              </p>
            )}

            {formError ? <InlineError>{formError}</InlineError> : null}
            <div className="feature-company-workspace__inline-actions">
              <button
                type="submit"
                className="ui-form__button ui-form__button--primary"
                disabled={busy}
              >
                {busy ? "Створення…" : "Створити"}
              </button>
              <button
                type="button"
                className="action-btn action-btn--ghost"
                onClick={handleClose}
                disabled={busy}
              >
                Скасувати
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CompanyClientCreateModal;
